import {createContext, useContext, useEffect, useRef, useState} from 'react'
import {ICatalogStats, ISimilarConfig} from '@/components/catalog'
import {getEmbedding, parseItem} from '@/utils/media'
import {DataSourcesContext} from '.'
import {IDataSource, IDataSourceItem, IDataSourceItemMap} from '..'
import {AuthContext} from '@/features/auth'
import {useGet, useMutate} from 'restful-react'
import _ from 'lodash'
import {MessageContext} from '@/features/errors'
import {cosineSimilarity} from '@/utils/math'
import Fuse from 'fuse.js'

export interface IFileMap {
  [name: string]: IDataSourceItemMap
}

export interface ISearchParams {
  catalogDataSource: ILegacyCatalogContext['catalogDataSource']
}

export interface ILegacyCatalogContext {
  catalogDataSource: 'file' | 'api'
  apiCatalogName?: string
  catalogStats: ICatalogStats
  catalog: IDataSourceItem[]
  fileData?: IFileMap
  apiData?: IDataSourceItemMap
  preferences: {
    wideThumbnails: boolean
  }
  staticCategoricalsVocabulary?: IDataSourceItem
  querySimilarFromEmbedding: (
    embedding: number[],
    catalog?: string,
    conf?: ISimilarConfig
  ) => Promise<IDataSourceItem[]>
  computeSimilarFromItem: (
    item: IDataSourceItem,
    items?: IDataSourceItem[],
    conf?: ISimilarConfig
  ) => IDataSourceItem[]
  search: (q: string, searchParams?: ISearchParams) => Promise<IDataSourceItem[]>
  getItem: (id: string) => Promise<IDataSourceItem | undefined>
  ensureFetchCatalog: () => void
  showCatalogOverrideDialog: () => void
  getSimilar: (id: string, conf?: ISimilarConfig) => Promise<IDataSourceItem[]>
}

/** @deprecated */
export const LegacyCatalogContext = createContext<ILegacyCatalogContext>({
  catalogDataSource: 'api',
  catalog: [],
  catalogStats: {
    types: {},
    fields: {},
    fieldTypes: {},
    categories: {},
  },
  preferences: {
    wideThumbnails: true,
  },
  querySimilarFromEmbedding: () => {
    throw new Error('not implemented')
  },
  computeSimilarFromItem: () => {
    throw new Error('not implemented')
  },
  ensureFetchCatalog: () => {
    throw new Error('not implemented')
  },
  showCatalogOverrideDialog: () => {
    throw new Error('not implemented')
  },
  search: () => {
    throw new Error('not implemented')
  },
  getItem: () => {
    throw new Error('not implemented')
  },
  getSimilar: () => {
    throw new Error('not implemented')
  },
})

export interface ILegacyCatalogContextWrapProps {
  children: React.ReactNode
}

/**
 * @deprecated
 */
export function LegacyCatalogContextWrap(props: ILegacyCatalogContextWrapProps) {
  const {children} = props

  const {getCookies} = useContext(AuthContext)

  const {logError} = useContext(MessageContext)

  const {
    sourceConfigs,
    mergedItems,
    mergedItemsStats,
    sourceData,
    activeCatalogName,
    showDialog,
    ensureFetch,
  } = useContext(DataSourcesContext)

  const fuseRef = useRef<Fuse<IDataSourceItem> | null>(null)

  const [catalogDataSource, setCatalogDataSource] = useState<'file' | 'api'>('api')
  const [apiData, setApiData] = useState<IDataSourceItemMap>(sourceData.catalog[activeCatalogName])
  const [fileData, setFileData] = useState<IFileMap>(getCurrentFileData())

  const {data: staticCategoricalsVocabulary} = useGet<IDataSourceItem>({
    path: '/assets/categoricals-vocabulary.json',
  })

  const {refetch: fetchItem} = useGet({
    lazy: true,
    path: ({id}) => `/api/gfp-result/items/${id}`,
    queryParams: {
      catalog: activeCatalogName,
    },
  })

  const {refetch: fetchSimilar} = useGet({
    lazy: true,
    path: '/api/gfp-result/similar/',
  })

  const {mutate: fetchSimilarByEmbedding} = useMutate<{similar: IDataSourceItem[]}>({
    path: '/api/gfp-result/embedding/similar',
    verb: 'POST',
    queryParams: {
      catalog: activeCatalogName,
    },
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),
  })

  const {refetch: fetchSearch} = useGet({
    lazy: true,
    path: '/api/gfp-result/search',
  })

  useEffect(() => {
    const fuse = new Fuse(mergedItems, {
      shouldSort: true,
      threshold: 0.3,
      location: 0,
      distance: 100,
      minMatchCharLength: 1,
      keys: ['title'],
    })
    fuseRef.current = fuse
  }, [mergedItems])

  useEffect(() => {
    const [_src, _key] = _.chain(sourceConfigs)
      .map((args) => {
        const {sourceID: id, key} = args
        if (id === 'catalog') {
          return [id, key] as [(typeof args)['sourceID'], string]
        } else if (id === 'localFile') {
          return [id, key] as [(typeof args)['sourceID'], string]
        }
      })
      .compact()
      .unzip()
      .map(_.first)
      .value() as [IDataSource<any>['sourceID'] | undefined, string | undefined]
    if (_src === 'catalog') {
      setCatalogDataSource('api')
      setApiData(_key ? sourceData.catalog[_key] : {})
      setFileData({})
    } else if (_src === 'localFile') {
      setCatalogDataSource('file')
      setApiData({})
      setFileData(getCurrentFileData())
    }
  }, [sourceConfigs, sourceData])

  function getCurrentFileData(): {[name: string]: IDataSourceItemMap} {
    return _.chain(sourceConfigs)
      .filter(({sourceID}) => sourceID === 'localFile')
      .map((cfg) => [cfg.key, sourceData[cfg.sourceID][cfg.key]])
      .fromPairs()
      .value()
  }

  function getFileItems(id: string): IDataSourceItem[] {
    const res = _.chain(fileData)
      .values()
      .map((v) => _.get(v, id))
      .compact()
      .value()
    return res
  }

  async function getItem(id: string): Promise<IDataSourceItem | undefined> {
    if (catalogDataSource === 'file') {
      const res = _.first(getFileItems(id))
      return res
    } else {
      return await fetchItem({
        pathParams: {id},
      })
    }
  }

  function computeSimilarFromItem(
    item: IDataSourceItem,
    _items?: IDataSourceItem[],
    conf?: ISimilarConfig
  ): IDataSourceItem[] {
    const {limit, addItems, normalize} = conf || {}
    const itemEmbedding: number[] | undefined = getEmbedding(item)
    const _invalid_items: IDataSourceItem[] = []
    const _catalog_filtered = _.chain(_items || mergedItems)
      .concat(...(addItems || []))
      .filter((c) => {
        const _e = getEmbedding(c)
        if (!(_e && _e.length > 0)) return false
        // verify that embedding actually contain numbers
        if (
          _.chain(_e)
            .filter((v) => !_.isNumber(v))
            .value().length > 0
        ) {
          _invalid_items.push(c)
          return false
        }
        return true
      })
      .value()
    if (_invalid_items.length > 0) {
      logError(
        `${_invalid_items.length} items with missing or invalid embeddings seen`,
        _invalid_items
      )
    }
    const embeddings: (number[] | undefined)[] = _.chain(_catalog_filtered)
      .map(getEmbedding)
      .value()
    if (!itemEmbedding || !embeddings) return []
    const res = _.chain(cosineSimilarity(itemEmbedding, embeddings, limit, normalize))
      .map(([i, score]) => ({
        ..._.get(_catalog_filtered, i),
        score: score,
      }))
      .value()
    return res
  }

  async function querySimilarFromEmbedding(
    embedding: number[],
    catalog?: string,
    _conf?: ISimilarConfig
  ): Promise<IDataSourceItem[]> {
    const res = await fetchSimilarByEmbedding(embedding, {
      queryParams: {
        catalog: catalog,
      },
    })
    return _.map(res.similar, (v, i) => parseItem(v, i))
  }

  async function getSimilar(id: string, conf?: ISimilarConfig): Promise<IDataSourceItem[]> {
    const {includeGFP} = {
      includeGFP: true,
      ...conf,
    }
    if (catalogDataSource === 'file') {
      const a = await getItem(id)
      if (!a) return []
      return computeSimilarFromItem(a, undefined, conf)
    } else {
      const res = await fetchSimilar({
        queryParams: {
          id,
          catalog: activeCatalogName,
          include_gfp: includeGFP,
        },
      })
      if (!(res && res.similar)) return []
      return _.map((res.similar as []) || [], (v, i) => parseItem(v, i))
    }
  }

  async function search(q: string, searchParams?: ISearchParams): Promise<IDataSourceItem[]> {
    const _src = searchParams?.catalogDataSource || catalogDataSource
    if (!q) return []
    if (_src === undefined) {
      console.error('Data source is undefined')
      return []
    }
    if (_src === 'file' && fuseRef.current) {
      return _.map(fuseRef.current.search(q), (v) => v.item)
    } else {
      // API search
      const res = (await fetchSearch({
        queryParams: {
          q,
          catalog: activeCatalogName,
        },
      })) as []
      return _.map(res, (v, i) => parseItem(v, i))
    }
  }

  if (!catalogDataSource || !staticCategoricalsVocabulary) return

  return (
    <LegacyCatalogContext.Provider
      value={{
        catalogDataSource,
        apiCatalogName: activeCatalogName,
        catalog: mergedItems,
        catalogStats: mergedItemsStats,
        preferences: {
          wideThumbnails: true,
        },
        staticCategoricalsVocabulary,
        apiData,
        fileData,
        querySimilarFromEmbedding,
        getSimilar,
        computeSimilarFromItem,
        getItem,
        search,
        ensureFetchCatalog: ensureFetch,
        showCatalogOverrideDialog: showDialog,
      }}
    >
      {children}
    </LegacyCatalogContext.Provider>
  )
}
